import React, { useState } from "react";
import useDebounce from "../../lib/hooks/useDebounce";
import gql from "graphql-tag";
import { Query, QueryResult } from "react-apollo";
import {
  Container,
  Heading,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  Input,
  InputGroup,
  InputLeftAddon,
  Button,
  CSSReset,
} from "@chakra-ui/react";
import { Search2Icon } from "@chakra-ui/icons";
import { Waypoint } from "react-waypoint";
import { RouteComponentProps } from "react-router";
import { FaUserCog, FaEdit } from "react-icons/fa";
import moment from "moment-timezone";
import { Link } from "react-router-dom";

export const ADMIN_SEARCH_MODELS = gql`
  query AdminSearchModels(
    $offset: Int
    $dataset: String
    $searchKeyword: String
  ) {
    adminSearchModels(
      offset: $offset
      dataset: $dataset
      searchKeyword: $searchKeyword
    ) {
      id
      displayName
      dataset
      launchDate
      roles
      embargoEndDate
    }
  }
`;

const SearchModels = (props: SearchModelProps): JSX.Element => {
  const [filterDataset, setFilterDataset] = useState("");
  const {
    value: searchKeyword,
    deferredValue: deferredKeyword,
    setValue: setKeyword,
  } = useDebounce(250);

  const queryVariables = {
    dataset: filterDataset,
    searchKeyword: deferredKeyword,
    offset: 0,
  };

  const globalWindow = typeof window !== "undefined" ? window : null;

  const canCreateEditModel = props.viewer.roles.find(
    (role: string): boolean => {
      return role === "ETR Master Admin" || role === "ETR Research Admin";
    }
  );

  return (
    <Container maxW={1920}>
      <CSSReset />
      <Flex mb={7} alignItems="center">
        <Heading as="h1" size="xl" mb={0}>
          Search Models
        </Heading>
        {canCreateEditModel ? (
          <Link to="/models/new" style={{ marginLeft: "auto" }}>
            <Button colorScheme={"green"} data-cy="create-model-button">
              Create New Model
            </Button>
          </Link>
        ) : null}
      </Flex>
      <Flex gridGap={4} mb={7}>
        <InputGroup flex={3}>
          <InputLeftAddon>
            <Search2Icon />
          </InputLeftAddon>
          <Input
            value={searchKeyword}
            placeholder="Keyword Search"
            onChange={(e) => setKeyword(e.target.value)}
          />
        </InputGroup>
        <Select flex={1} onChange={(e) => setFilterDataset(e.target.value)}>
          <option value="">Filter by dataset</option>
          <option value={"DRILLDOWN"}>DrillDown</option>
        </Select>
      </Flex>
      <Query
        query={ADMIN_SEARCH_MODELS}
        variables={queryVariables}
        fetchPolicy="network-only"
      >
        {({
          data,
          error,
          fetchMore,
        }: QueryResult<SearchModelResults, SearchModelQueryVariables>) => {
          if (error) {
            return <Text>Error while getting model.</Text>;
          }

          if (data?.adminSearchModels) {
            const onLoadMore = () => {
              fetchMore({
                variables: { offset: data.adminSearchModels.length },
                updateQuery: (previousResults, { fetchMoreResult }) => {
                  if (!fetchMoreResult?.adminSearchModels.length) {
                    return previousResults;
                  }
                  return {
                    adminSearchModels: [
                      ...previousResults.adminSearchModels,
                      ...fetchMoreResult.adminSearchModels,
                    ],
                  };
                },
              });
            };
            return (
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Embargo End Date</Th>
                      <Th>Display Name</Th>
                      <Th>Dataset</Th>
                      <Th>Launch Date</Th>
                      {canCreateEditModel ? (
                        <Th data-cy="edit-model-header">Edit</Th>
                      ) : null}
                      <Th textAlign={"center"}>Assign User Access</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.adminSearchModels.map((model, idx) => {
                      const isExclusive = !model.roles.includes("DrillDowns");
                      const isEmbargoed =
                        !!model.embargoEndDate &&
                        moment().isBefore(model.embargoEndDate);
                      return (
                        <Tr key={model.id}>
                          <Td>
                            {isExclusive
                              ? "Exclusive"
                              : isEmbargoed
                              ? moment
                                  .tz(model.embargoEndDate, moment.tz.guess())
                                  .format("MMM D YYYY, h:mma z")
                              : ""}
                          </Td>
                          <Td
                            title={model.displayName}
                            style={{
                              maxWidth: "400px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {model.displayName}
                          </Td>
                          <Td>{model.dataset}</Td>
                          <Td>
                            {moment
                              .tz(model.launchDate, moment.tz.guess())
                              .format("MMM D YYYY, h:mma z")}
                          </Td>
                          {canCreateEditModel ? (
                            <Td data-cy="edit-model-icon" textAlign={"center"}>
                              <Link
                                to={`/models/${model.id}/edit`}
                                style={{ color: "black" }}
                              >
                                <FaEdit />
                              </Link>
                            </Td>
                          ) : null}
                          <Td textAlign={"center"}>
                            <Link
                              to={`/model/${model.id}/access`}
                              style={{ color: "black" }}
                            >
                              <FaUserCog />
                            </Link>
                          </Td>
                          <Td></Td>
                          {idx === data.adminSearchModels.length - 3 ? (
                            <Td>
                              <Waypoint
                                scrollableAncestor={globalWindow}
                                onEnter={onLoadMore}
                              />
                            </Td>
                          ) : (
                            <Td></Td>
                          )}
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            );
          }
          return null;
        }}
      </Query>
    </Container>
  );
};

export default SearchModels;

interface Model {
  id: string;
  displayName: string;
  launchDate: string;
  dataset: string;
  roles: string[];
  embargoEndDate: string;
}

interface SearchModelQueryVariables {
  searchKeyword: string;
  dataset: string;
  offset: number;
}

interface SearchModelResults {
  adminSearchModels: Model[];
}

interface SearchModelProps extends RouteComponentProps {
  viewer: {
    roles: string[];
  };
}
